import type { Building } from "building-types/building/buildingTypes";
import type { DataSequenceUnit } from "building-types/dataSequences/dataSequenceUnitTypes";
import type { BuildingEntityFilter } from "building-types/dataSequences/filterableBuildingEntityTypes";
import { AggregationMethod } from "building-types/dataSequences/measures";
import type {
  AggregatedKeyAttributesAndMeasures,
  EntityKeyAttributes,
  EntityKeyMeasures,
  KeyAttribute,
  KeyAttributesAndMeasures,
  KeyAttributesAndMeasuresOptions,
  KeyMeasure,
  KeyAttributesAndMeasuresSortOrder,
  KeyMeasureOptions,
  KeyAttributeOptions,
} from "building-types/dataSequences/measures";
import type { BuiltInDataTableSettings } from "building-types/dataSequences/scheduleSettingsTable";
import { dataSequenceUnitConversionFunctions } from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import {
  keyAttributeOptionsNew,
  keyMeasureOptionsNew,
  DEFAULT_KEY_MEASURE_OPTIONS,
  keyMeasureNew,
  keyAttributeNew,
  DEFAULT_KEY_ATTRIBUTE_OPTIONS,
} from "doc/utils/dataSequenceTools";
import type { BuiltInKAMDataSequence, BuiltInKAMDataSequenceSelector } from "./dataSequenceCatalog";
import type { FindAndReplaceFunc } from "./dataTableTypes";

export function makeBuiltInKAMDataSequence<T extends SystemDataSequenceEntityType>(
  building: Building,
  settings: BuiltInDataTableSettings,
  selector: BuiltInKAMDataSequenceSelector<T>,
  findAndReplace: FindAndReplaceFunc,
  filter?: BuildingEntityFilter,
): BuiltInKAMDataSequence<T> {
  const type = selector.type;

  const newSelector = updateBuiltInKAMDataSequenceSelectorFromSettings(selector, settings);
  const KAMs = newSelector.selector(building, filter);

  const nonMatchingType = KAMs.find((KAM) => KAM.type !== type)?.type;
  if (nonMatchingType) {
    throw Error(`Cannot create KAM group of different KAM types! Expected ${type} but found ${nonMatchingType}`);
  }

  return {
    type,
    kams: KAMs,
    aggregatedKAMs: aggregateKeyAttributesAndMeasures(KAMs, newSelector.options, newSelector.sortOrder, findAndReplace),
  };
}

function updateBuiltInKAMDataSequenceSelectorFromSettings<T extends SystemDataSequenceEntityType>(
  selector: BuiltInKAMDataSequenceSelector<T>,
  settings: BuiltInDataTableSettings,
): BuiltInKAMDataSequenceSelector<T> {
  const baseOptions = fillBuiltInDataSequenceKAMOptionDefaults(selector, true);

  const KAMAttributes: Partial<Record<keyof EntityKeyAttributes, KeyAttributeOptions>> = {};
  for (const attribute in settings.attributes) {
    const baseAttribute = settings.attributes[attribute];
    KAMAttributes[attribute as Extract<keyof EntityKeyAttributes, string>] = {
      ...baseAttribute,
      unit: baseAttribute.unit,
    };
  }

  const KAMMeasures: Partial<Record<keyof EntityKeyMeasures, KeyMeasureOptions>> = {};
  for (const measure in settings.measures) {
    const baseMeasure = settings.measures[measure];
    KAMMeasures[measure as Extract<keyof EntityKeyMeasures, string>] = {
      ...baseMeasure,
      unit: baseMeasure.unit,
    };
  }

  const KAMOptions: KeyAttributesAndMeasuresOptions<EntityKeyAttributes, EntityKeyMeasures> = {
    ...baseOptions,
    attributes: { ...baseOptions.attributes, ...KAMAttributes },
    measures: { ...baseOptions.measures, ...KAMMeasures },
  };

  const KAMDefiningKeys = getDefiningKeys(KAMOptions);

  return {
    ...selector,
    options: KAMOptions,
    definesEntityAttributes: KAMDefiningKeys,
  };
}

export function fillBuiltInDataSequenceKAMOptionDefaults<T extends SystemDataSequenceEntityType>(
  selector: BuiltInKAMDataSequenceSelector<T>,
  scheduleDefaults: boolean = false,
): KeyAttributesAndMeasuresOptions<EntityKeyAttributes, EntityKeyMeasures> {
  const KAMOptions = selector.options;
  const definesEntityOptions = selector.definesEntityAttributes;
  const emptyKAM = selector.emptyKAM;

  for (const attribute in emptyKAM.attributes) {
    if (scheduleDefaults) {
      if (!KAMOptions.attributes[attribute]) {
        KAMOptions.attributes[attribute] = keyAttributeOptionsNew({
          unit: emptyKAM.attributes[attribute]?.unit,
        });
      }
    } else {
      KAMOptions.attributes[attribute] = keyAttributeOptionsNew({
        unit: emptyKAM.attributes[attribute]?.unit,
        definesEntity: definesEntityOptions.includes(attribute),
      });
    }
  }
  for (const measure in emptyKAM.measures) {
    if (!KAMOptions.measures[measure]) {
      KAMOptions.measures[measure] = keyMeasureOptionsNew({
        unit: emptyKAM.measures[measure]?.unit,
      });
    }
  }
  return KAMOptions;
}

/**
 * Reduces an array of Key Attributes and Measures (KAMs) by producing a hash of their attributes and
 * aggregating the measures of matching entries.
 * @param KAMs - The Key Attributes and Measures to be reduced/aggregated
 * @returns A new array of KAMs that contains no duplicates (as defined by their attributes).
 * These KAMs are a deep copy of the originals.
 */
function aggregateKeyAttributesAndMeasures<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  KAMs: KeyAttributesAndMeasures<Attributes, Measures>[],
  options: KeyAttributesAndMeasuresOptions<Attributes, Measures>,
  sortOrder: KeyAttributesAndMeasuresSortOrder<Attributes, Measures>,
  findAndReplace: FindAndReplaceFunc,
): AggregatedKeyAttributesAndMeasures<Attributes, Measures>[] {
  const completedAttributeIndicesByHash: { [hash: string]: number } = {};

  const result: AggregatedKeyAttributesAndMeasures<Attributes, Measures>[] = KAMs.sort(sortOrder).reduce(
    (aggregated, current) => {
      // Enforce the options on the current KAM and copy to new KAM
      const copiedKAM = enforceKAMOptionsWithDeepCopy(current, options);

      // Hash the KAM's attributes
      const definingAttributesHash = hashKAMAttributes(copiedKAM, options, findAndReplace);

      // If current attribute hash exists, pull out existing KAM and aggregate to it
      if (completedAttributeIndicesByHash[definingAttributesHash] !== undefined) {
        const existing = aggregated[completedAttributeIndicesByHash[definingAttributesHash]];
        // Aggregate measures
        for (const currentMeasuresKey in copiedKAM.measures) {
          const currentMeasure = copiedKAM.measures[currentMeasuresKey];
          const measureOption = options.measures[currentMeasuresKey] ?? DEFAULT_KEY_MEASURE_OPTIONS;
          if (measureOption.isAggregated) {
            const existingMeasure = existing.measures[currentMeasuresKey];
            // If matching measure exists, apply aggregation strategy
            if (existingMeasure) {
              switch (measureOption.aggregationMethod) {
                case AggregationMethod.SUM:
                  existing.measures[currentMeasuresKey] = {
                    ...existingMeasure,
                    value: existingMeasure.value + currentMeasure.value,
                  };
                  break;
                case AggregationMethod.TAKEFIRST:
                  // Do nothing - keep existing value
                  break;
              }
            }
            // If existing measure doesn't exist, add it
            else {
              existing.measures[currentMeasuresKey] = keyMeasureNew(
                currentMeasure.value,
                currentMeasure.unit,
              ) as Measures[Extract<keyof Measures, string>];
            }
          }
        }
        // Aggregate the entities the merged rows were derived from
        const seen = new Set(existing.handles.map(docEntityHandleHash));
        for (const handle of copiedKAM.handles) {
          const hash = docEntityHandleHash(handle);
          if (!seen.has(hash)) {
            seen.add(hash);
            existing.handles.push(handle);
          }
        }
      }
      // If current attribute hash does not exist, add current KAM to results
      else {
        const index = aggregated.push({ ...copiedKAM, definingAttributesHash }) - 1;
        completedAttributeIndicesByHash[definingAttributesHash] = index;
      }
      return aggregated;
    },
    [] as AggregatedKeyAttributesAndMeasures<Attributes, Measures>[],
  );

  return result;
}

function enforceKAMOptionsWithDeepCopy<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  KAM: KeyAttributesAndMeasures<Attributes, Measures>,
  options: KeyAttributesAndMeasuresOptions<Attributes, Measures>,
): KeyAttributesAndMeasures<Attributes, Measures> {
  const copiedAttributes = {} as Attributes;
  for (const attrName in KAM.attributes) {
    const optionsUnit = options.attributes[attrName]?.unit;
    try {
      const unitConvertedValue = handleAttributeUnitDifferences({ unit: optionsUnit }, KAM.attributes[attrName]);
      copiedAttributes[attrName] = keyAttributeNew(unitConvertedValue, optionsUnit) as Attributes[Extract<
        keyof Attributes,
        string
      >];
    } catch (error) {
      console.error(`Error enforcing KAM options for attribute '${attrName}': ${error}`, { options, KAM });
      throw error;
    }
  }
  const copiedMeasures = {} as Measures;
  for (const measureName in KAM.measures) {
    const optionsUnit = options.measures[measureName]?.unit;
    const unitConvertedValue = handleMeasureUnitDifferences(
      { unit: optionsUnit ?? "QUANTITY" },
      KAM.measures[measureName],
    );
    copiedMeasures[measureName] = keyMeasureNew(unitConvertedValue, optionsUnit) as Measures[Extract<
      keyof Measures,
      string
    >];
  }
  return {
    type: KAM.type,
    attributes: copiedAttributes,
    measures: copiedMeasures,
    handles: [...KAM.handles],
  };
}

/**
 * Compares the units of Attributes A and B and converts the value of B to the units of A, if necessary.
 * If A and B both do not have units, it will just return B's value unchanged.
 * If A or B have a unit and the other does not, it will throw an error.
 * @param a a Key Attribute whose units we will preference
 * @param b a Key Attribute whose units we will convert to the units of KA A
 * @throws Error if A and B have conflicting units (e.g. QUANTITY and SQ FT) or if B's value is NaN
 * @returns The value of KA B in the units of KA A
 */
function handleAttributeUnitDifferences(a: Pick<KeyAttribute, "unit">, b: KeyAttribute): string {
  if (a.unit && b.unit && !isNaN(Number(b.value))) {
    return doUnitConversion(a.unit, b.unit, Number(b.value)).toString();
  } else if (!a.unit && !b.unit) {
    return b.value;
  } else if (isNaN(Number(b.value))) {
    throw new Error(`Could not perform unit conversion on ${b.value}; not a number!`);
  } else {
    throw new Error(`Could not convert ${b.unit} to ${a.unit}; missing a unit!`);
  }
}

/**
 * Compares the units of Measures A and B and converts the value of B to the units of A, if necessary
 * @param a a Key Measure whose units we will preference
 * @param b a Key Measure whose units we will convert to the units of KM A
 * @throws Error if A and B have conflicting units (e.g. QUANTITY and SQ FT)
 * @returns The value of KM B in the units of KM A
 */
function handleMeasureUnitDifferences(a: Pick<KeyMeasure, "unit">, b: KeyMeasure): number {
  return doUnitConversion(a.unit, b.unit, b.value);
}

function doUnitConversion(unitA: DataSequenceUnit, unitB: DataSequenceUnit, value: number): number {
  if (dataSequenceUnitConversionFunctions[unitB].hasOwnProperty(unitA)) {
    assertIsNotUndefined(dataSequenceUnitConversionFunctions[unitB][unitA]);
    return dataSequenceUnitConversionFunctions[unitB][unitA](value);
  } else {
    throw new Error(`Could not convert ${unitB} to ${unitA}; no valid conversion!`);
  }
}

function hashKAMAttributes<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  KAM: KeyAttributesAndMeasures<Attributes, Measures>,
  options: KeyAttributesAndMeasuresOptions<Attributes, Measures>,
  findAndReplace: FindAndReplaceFunc,
): string {
  const definingAttributes = getDefiningAttributes(KAM, options);

  // Create a hash of key:values+unit
  let hash = KAM.type + "__";
  Object.keys(definingAttributes)
    // order of keys must be consistent
    .sort()
    .forEach((key, i) => {
      if (i > 0) {
        hash += ",";
      }
      hash += `${key}:`;

      if (definingAttributes[key].value) {
        hash += findAndReplace(definingAttributes[key].value, key, false);
      }
      if (definingAttributes[key].unit) {
        hash += definingAttributes[key].unit;
      }
    }, {});

  return hash;
}

// Filter attributes to those that define the entity
function getDefiningAttributes<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  KAM: KeyAttributesAndMeasures<Attributes, Measures>,
  options: KeyAttributesAndMeasuresOptions<Attributes, Measures>,
) {
  const definingAttributes: Record<string, Attributes[Extract<keyof Attributes, string>]> = {};
  for (const key in KAM.attributes) {
    const option = options.attributes[key] ?? DEFAULT_KEY_ATTRIBUTE_OPTIONS;
    // Filter out hidden attributes so that rows showing columns with the
    // exact same data will be collapsed and aggregated together
    if (option.isHidden) {
      continue;
    }
    if (option.definesEntity) {
      definingAttributes[key] = KAM.attributes[key];
    }
  }

  return definingAttributes;
}

function getDefiningKeys<Attributes extends EntityKeyAttributes, Measures extends EntityKeyMeasures>(
  options: KeyAttributesAndMeasuresOptions<Attributes, Measures>,
) {
  const definingKeys: string[] = [];
  if (!options) {
    return definingKeys;
  }
  for (const key in options.attributes) {
    const option = options.attributes[key];
    if (option && option.definesEntity) {
      definingKeys.push(key);
    }
  }
  return definingKeys;
}
